import { SelectV4, TextInputV4, Truncate } from '@labrav/react-components'
import {
  AzureOfferPlan,
  AzurePricingType,
  CustomMeters,
  Installments,
} from '../../../../modules/privateOffer/reducer'
import { Product } from '../../../../modules/productsListing/reducer'
import { get } from 'lodash'
import { FormikProps, useFormikContext } from 'formik'
import { DataType } from '@labrav/react-components/lib/@types/types/forms'
import { useStyles } from './PlanRow.styles'
import { Grid, SelectChangeEvent } from '@mui/material'
import { convertProductPlanToOfferPlan } from './ContractSelection/ContractSelection'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash } from '@fortawesome/pro-regular-svg-icons'
import {
  getValidValueBetweenMinMax,
  isNonEmptyNumber,
} from '../../../../../common/utils/validateMinMax'
import { getDiscountedPriceValue } from '../../../../../common/utils/getDiscountedPriceValue'
import { getInstallmentsBasedOnSchedule } from '../../../../utils/resaleAuthorizationUtil'
import { ScheduleType } from '../../../../modules/privateOffer/reducer'
import { getDurationValueFromBillingTerm } from './PlanRowComponent'
import { cloneDeep } from 'lodash'
import { PlanRowComponent } from './PlanRowComponent'
import { useCallback } from 'react'

export const PlanRow = ({
  plan,
  index,
  product,
  planIds,
  remove,
}: {
  plan: AzureOfferPlan
  index: number
  product: Product
  planIds: string[]
  remove?: <X = any>(index: number) => X | undefined
}) => {
  const { classes } = useStyles()
  const { values, touched, errors, handleBlur, setFieldValue } =
    useFormikContext() as FormikProps<Record<string, unknown>>

  const plans = (values?.plans || []) as AzureOfferPlan[]

  const azurePricingType = values?.azurePricingType

  const isDiscountPricing =
    azurePricingType === AzurePricingType.LABRA_PERCENTAGE

  const productPlans = product?.plans
    ?.filter(plan => !planIds.includes(plan?.planId))
    ?.filter(
      plan => !(plan?.isPrivate || plan?.isStopSell || plan?.hasFreeTrials)
    )
    .map(plan => ({
      label: plan?.displayName,
      value: plan?.planId,
    }))

  const handleChangePlanSelection = (e: SelectChangeEvent) => {
    const selectedPlan = product?.plans?.find(
      item => item?.planId === e.target.value
    )
    if (selectedPlan) {
      const offerPlan = convertProductPlanToOfferPlan(selectedPlan)

      setFieldValue(`plans.${[index]}`, {
        ...offerPlan,
        discountPercentage: null,
      })
    }
  }

  const updateTcvAndInstallments = useCallback(
    ({ discountPercentage }: { discountPercentage?: number }) => {
      const originalPricingPrices =
        plan?.originalPricing?.recurrentPrice?.prices?.filter(
          item =>
            item?.billingTerm?.type ===
              plan.pricing.recurrentPrice.prices[0].billingTerm.type &&
            item?.billingTerm?.value ===
              plan.pricing.recurrentPrice.prices[0].billingTerm.value
        ) || []

      if (originalPricingPrices.length > 0) {
        const originalPrice = originalPricingPrices[0].pricePerPaymentInUsd || 0

        if (isNonEmptyNumber(originalPrice) && originalPrice > 0) {
          const discountedTcv = getDiscountedPriceValue({
            price: originalPrice,
            discount: discountPercentage || 0,
          })

          const mppoDuration = getDurationValueFromBillingTerm(
            plan.pricing.recurrentPrice.prices[0].billingTerm
          )

          const isFlexiblePayment =
            plan.pricing.recurrentPrice.prices[0].installments?.schedule?.length

          const updatedCustomMeters = getUpdatedCustomMeters(
            plan,
            discountPercentage,
            plan?.pricing?.customMeters
          )

          if (isFlexiblePayment) {
            const installments = getInstallmentsBasedOnSchedule({
              tcv: discountedTcv,
              durationInMonths: mppoDuration || 0,
              paymentSchedule:
                plan.pricing.recurrentPrice.prices[0].installments?.frequency ||
                ScheduleType.MONTHLY,
              now:
                (values?.offerStartDate as string) || new Date().toISOString(),
            })

            const updatedPlan = {
              ...plan,
              discountPercentage: discountPercentage,
              pricing: {
                ...plan.pricing,
                recurrentPrice: {
                  ...plan.pricing.recurrentPrice,
                  prices: [
                    {
                      ...plan.pricing.recurrentPrice.prices[0],
                      installments: {
                        ...plan.pricing.recurrentPrice.prices[0].installments,
                        tcv: discountedTcv,
                        schedule: installments as unknown as Installments[],
                      },
                    },
                  ],
                },
                customMeters: updatedCustomMeters,
              },
            }

            const plans = [...(values.plans as AzureOfferPlan[])]
            plans[index] = cloneDeep(updatedPlan)
            setFieldValue('plans', [...cloneDeep(plans)])
          } else {
            const updatedPlan = {
              ...plan,
              discountPercentage: discountPercentage,
              pricing: {
                ...plan.pricing,
                recurrentPrice: {
                  ...plan.pricing.recurrentPrice,
                  prices: [
                    {
                      ...plan.pricing.recurrentPrice.prices[0],
                      pricePerPaymentInUsd: discountedTcv,
                    },
                  ],
                },
                customMeters: updatedCustomMeters,
              },
            }
            const plans = [...(values.plans as AzureOfferPlan[])]
            plans[index] = cloneDeep(updatedPlan)
            setFieldValue('plans', [...cloneDeep(plans)])
          }
        }
      }
    },
    [values, setFieldValue, index]
  )

  const handleDiscountUpdate = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = getValidValueBetweenMinMax(
      event.target.value as string,
      1,
      100
    )
    await updateTcvAndInstallments({
      discountPercentage: value || undefined,
    })
  }

  return (
    <>
      <div className={classes.planRowWrapper}>
        <div className={classes.header}>
          <div className={classes.plansHeading}>Plans</div>
          <div>
            {plans.length > 1 && (
              <FontAwesomeIcon
                icon={faTrash}
                style={{
                  width: '14px',
                  height: '20px',
                  color: '#6A777A',
                  cursor: 'pointer',
                }}
                data-testid="trash-metaData"
                onClick={() => remove && remove(index)}
              />
            )}
          </div>
        </div>
        <Grid container width={'100%'} spacing={3}>
          <Grid size={4}>
            <div className={classes.planSelect}>
              <SelectV4
                field={{
                  title: 'Select plan',
                  placeHolder: 'Select plan',
                  humanReadableKey: 'plan',
                  id: `plans.${[index]}.labraPlanId`,
                  isRequired: false,
                  isReadOnly: false,
                  isCustom: false,
                  defaultValue: '',
                  dataType: 'enum' as DataType,
                  options: productPlans,
                }}
                size="small"
                value={plan?.displayName || ''}
                onChange={handleChangePlanSelection}
                onBlur={handleBlur}
                isError={
                  !!(
                    get(touched, `plans.${[index]}.labraPlanId`, false) &&
                    get(errors, `plans.${[index]}.labraPlanId`, false)
                  )
                }
                errorText={
                  (!!get(touched, `plans.${[index]}.labraPlanId`, false) &&
                    get(errors, `plans.${[index]}.labraPlanId`, '')) ||
                  ''
                }
              />
            </div>
          </Grid>
          {plan?.labraPlanId && (
            <>
              <Grid size={isDiscountPricing ? 5 : 6}>
                <div className={classes.title}>Description</div>
                <div className={classes.content}>
                  <Truncate showIcon={false} clipboardTooltip>
                    {plan.description}
                  </Truncate>
                </div>
              </Grid>
              {isDiscountPricing && (
                <Grid size={3}>
                  <div className={classes.content}>
                    <TextInputV4
                      keyName={`plans.${[index]}.discountPercentage`}
                      title="Discount (%)"
                      type="number"
                      placeHolder=""
                      alignRight
                      minNumber={1}
                      maxNumber={100}
                      value={plan?.discountPercentage || ''}
                      updatedValue={plan?.discountPercentage || ''}
                      isError={
                        !!(
                          get(
                            touched,
                            `plans.${[index]}.discountPercentage`,
                            false
                          ) &&
                          get(
                            errors,
                            `plans.${[index]}.discountPercentage`,
                            false
                          )
                        )
                      }
                      onChange={handleDiscountUpdate}
                      isReadOnly={false}
                      isRequired={false}
                      errorText={
                        (!!get(
                          touched,
                          `plans.${[index]}.discountPercentage`,
                          false
                        ) &&
                          get(
                            errors,
                            `plans.${[index]}.discountPercentage`,
                            ''
                          )) ||
                        ''
                      }
                      showSpinner={false}
                      onBlur={handleBlur}
                    />
                  </div>
                </Grid>
              )}
            </>
          )}
        </Grid>
        {plan?.labraPlanId && (
          <PlanRowComponent index={index} plan={plan} product={product} />
        )}
      </div>
    </>
  )
}

export const getUpdatedCustomMeters = (
  plan: AzureOfferPlan,
  discountPercentage?: number,
  customMeters?: CustomMeters
) => {
  let updatedCustomMeters = customMeters
  if (customMeters?.meters && Object.keys(customMeters?.meters).length > 0) {
    const originalCustomMeters = plan.originalPricing?.customMeters?.meters
    if (originalCustomMeters) {
      const updatedMeters = { ...customMeters?.meters }

      Object.keys(updatedMeters).forEach(meterKey => {
        const originalMeter = originalCustomMeters[meterKey]
        if (originalMeter?.pricePerPaymentInUsd) {
          const discountedMeterPrice = getDiscountedPriceValue({
            price: originalMeter.pricePerPaymentInUsd,
            discount: discountPercentage || 0,
          })

          updatedMeters[meterKey] = {
            ...updatedMeters[meterKey],
            pricePerPaymentInUsd: discountedMeterPrice,
          }
        }
      })

      updatedCustomMeters = {
        ...customMeters,
        meters: updatedMeters,
      }
    }
  }
  return updatedCustomMeters
}
